import AHDate from '_shared/dates/date';
import { useDraggable } from '@dnd-kit/core';
import AssistantIcon from "@mui/icons-material/Assistant";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LastPageIcon from '@mui/icons-material/LastPage';
import SendIcon from "@mui/icons-material/Send";
import CircularProgress from "@mui/material/CircularProgress";
import Fab from "@mui/material/Fab";
import Grow from "@mui/material/Grow";
import IconButton from "@mui/material/IconButton";
import type { PopperPlacementType } from "@mui/material/Popper";
import Popper from "@mui/material/Popper";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { KeyboardKey } from '_shared/_utilities/keyboard-keys';
import { ResizeHandlePlacementContext, ResizeHandle } from '_shared/_views/handles/resize-handle';
import MarkdownRenderer from '_shared/_views/markdown/markdown-renderer';
import Guid from '_shared/guids/guid';
import AIAssistantAPIService, { AIAssistantAPIServiceError } from 'ai-assistant/_api/ai-assistant-api-service';
import AssistantResponse from 'ai-assistant/assistant-response';
import Citations from 'ai-assistant/citations/_views/citations';
import React from "react";
import { useLocation } from 'react-router';
import { useSession } from 'users/__session/session-context';
import { Role } from 'ai-assistant/roles/roles';

// #region Styled Components
const MainContainer = styled('div')<{
  dragging: boolean,
  leftPos: number,
  topPos: number,
  transformX?: number,
  transformY?: number,
  scaleX?: number,
  scaleY?: number
}>(({ dragging, leftPos, topPos, transformX, transformY, scaleX, scaleY }) => ({
  top: `${topPos}px`,
  left: `${leftPos}px`,
  position: 'fixed',
  transform: `translate3d(${transformX ?? 0}px, ${transformY ?? 0}px, 0) scale(${dragging ? 1.05 : scaleX}, ${dragging ? 1.05 : scaleY})`,
  zIndex: 2000,
}));
const ChatBubble = styled(Fab)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  border: '4px solid transparent',
  backgroundImage: 'linear-gradient(90deg,rgb(255, 255, 255) 0%, #bbdefb 100%), linear-gradient(90deg, #b3b9ff,rgba(0, 149, 255, 0.75))',
  backgroundOrigin: 'border-box',
  backgroundClip: 'padding-box, border-box',
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundImage: 'linear-gradient(90deg, #fff 0%, #e3f2fd 100%), linear-gradient(90deg,#0095ff, #0095ff)',
  },
}));
const ChatBubbleContent = styled('span')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-evenly',
  marginLeft: theme.spacing(1),
  minWidth: '110px'
}));
const ChatBubbleIcon = styled(
  'span',
  { shouldForwardProp: (prop) => prop !== 'rotate' && prop !== 'top' }
)<{ rotate: boolean, top: boolean }>(({ rotate, top }) => {
  let rotation: string;
  if (rotate) {
    rotation = top ? '90deg' : '-90deg';
  } else {
    rotation = '0deg';
  }
  return {
    color: 'rgb(0, 149, 255)',
    display: 'inline-flex',
    transition: 'transform 0.3s ease',
    transform: `rotate(${rotation})`,
    transformOrigin: 'center center',
  };
});
const ChatBubbleLabel = styled('span')(({ theme }) => ({
  display: 'inline-block',
  marginLeft: theme.spacing(0.5),
  whiteSpace: 'nowrap',
}));
const PopperContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  boxShadow: 'rgba(0, 0, 0, 0.19) 0px 10px 20px, rgba(0, 0, 0, 0.23) 0px 6px 6px;',
  display: 'grid',
  gridTemplateRows: 'auto 1fr auto',
  height: '550px',
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(2),
  minHeight: '550px',
  maxHeight: '75vh',
  minWidth: '450px',
  maxWidth: '80vw',
  overflow: 'hidden',
  padding: 0,
  position: 'relative',
  width: '450px'
}));
const DragHandle = styled(DragIndicatorIcon, {
  shouldForwardProp: (prop) => prop !== 'dragging'
})<{ dragging: boolean }>(({ dragging }) => ({
  cursor: dragging ? "grabbing" : "grab"
}));
const TopLeftResizeHandle = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  position: 'absolute',
  top: 0,
  left: 0,
  width: theme.spacing(2),
  height: theme.spacing(2),
  zIndex: 10,
}));
const TopResizeHandle = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  position: 'absolute',
  top: 0,
  left: theme.spacing(2),
  width: 'calc(100% - 32px)',
  height: theme.spacing(2),
  zIndex: 10,
}));
const TopRightResizeHandle = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  position: 'absolute',
  top: 0,
  right: 0,
  width: theme.spacing(2),
  height: theme.spacing(2),
  zIndex: 10,
}));
const RightResizeHandle = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  position: 'absolute',
  top: theme.spacing(2),
  right: 0,
  width: theme.spacing(2),
  height: 'calc(100% - 32px)',
  zIndex: 10,
}));
const BottomRightResizeHandle = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  position: 'absolute',
  bottom: 0,
  right: 0,
  width: theme.spacing(2),
  height: theme.spacing(2),
  zIndex: 10,
}));
const BottomResizeHandle = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  position: 'absolute',
  bottom: 0,
  left: theme.spacing(2),
  width: `calc(100% - ${theme.spacing(4)})`,
  height: theme.spacing(2),
  zIndex: 10,
}));
const BottomLeftResizeHandle = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: theme.spacing(2),
  height: theme.spacing(2),
  zIndex: 10,
}));
const LeftResizeHandle = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  position: 'absolute',
  top: theme.spacing(2),
  left: 0,
  width: theme.spacing(2),
  height: `calc(100% - ${theme.spacing(4)})`,
  zIndex: 10,
}));
const Header = styled('div')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.primary.main,
  borderTopLeftRadius: theme.spacing(1),
  borderTopRightRadius: theme.spacing(1),
  color: theme.palette.common.white,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  gap: theme.spacing(1),
  'h3': {
    fontWeight: 500,
  }
}));
const Content = styled('div')(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(2),
  paddingRight: theme.spacing(1),
  overflowX: 'hidden',
  overflowY: 'scroll',
  scrollbarGutter: 'stable',
}));
const MessageEditor = styled(TextField)(() => ({
  backgroundColor: '#f5f5f5',
  borderRadius: '0 0 8px 8px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '0 0 8px 8px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));
const Message = styled('div')<{ sent: boolean }>(
  ({ theme, sent }) => ({
    containerType: 'inline-size',
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: sent ? 'row-reverse' : 'row',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    '& .message-text': {
      backgroundColor: sent ? '#bdf' : '#f0f0f0',
      '@container (min-width: 0px)': {
        maxWidth: '90%'
      },
      '@container (min-width: 600px)': {
        maxWidth: '75%'
      },
      '@container (min-width: 800px)': {
        maxWidth: '66%'
      }
    },
  }));
const MessageIcon = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
  marginTop: theme.spacing(1),
}));
const MessageText = styled('div')(({ theme }) => ({
  backgroundColor: '#f0f0f0',
  borderRadius: '8px',
  color: theme.palette.text.primary,
  fontSize: '0.9rem',
  lineHeight: '1.5',
  marginBottom: theme.spacing(2),
  wordBreak: 'break-word',
  padding: theme.spacing(1.5, 2),
  "& p": {
    margin: 0,
  }
}));
const Timestamp = styled('div')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.6rem',
  marginTop: theme.spacing(0.5),
  width: '100%'
}));
const MarkdownStyles = styled('div')(({ theme }) => ({
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  '& code': {
    backgroundColor: theme.palette.background.default,
    borderRadius: '4px',
    padding: '2px 4px',
    fontFamily: 'monospace',
    fontSize: '0.9rem',
  },
  '& pre': {
    backgroundColor: theme.palette.background.default,
    borderRadius: '4px',
    padding: '10px',
    overflowX: 'auto',
    fontFamily: 'monospace',
    fontSize: '0.9rem',
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    paddingLeft: '10px',
    color: theme.palette.text.secondary,
    margin: '10px 0',
  },
  '& ul, & ol': {
    paddingLeft: '20px',
    margin: '10px 0',
    '& li': {
      marginBottom: '5px',
    },
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    margin: '10px 0',
    fontWeight: 'bold',
  },
  '& strong': {
    fontWeight: 'bold',
  },
  '& em': {
    fontStyle: 'italic',
  },
  '& hr': {
    border: 'none',
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: '20px 0',
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '4px',
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: theme.spacing(1),
    '& th, & td': {
      backgroundColor: theme.palette.background.default,
      border: `1px solid ${theme.palette.divider}`,
      fontSize: '0.9em',
      padding: '8px',
      textAlign: 'left',
      overflowWrap: 'break-word',
      wordBreak: 'normal',
      whiteSpace: 'normal',
    },
    '& th': {
      backgroundColor: theme.palette.background.default,
      fontSize: '0.8em',
      fontWeight: 'bold',
    },
  },
}));
const CloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  position: 'absolute',
  right: theme.spacing(1),
}));
// #endregion Styled Components

function PopperPlacementSync({
  placement,
  onChange,
}: {
  placement: PopperPlacementType;
  onChange: (p: PopperPlacementType) => void;
}) {
  React.useEffect(() => {
    onChange(placement);
  }, [placement, onChange]);
  return null;
}

interface AiAssistantProps {
  leftPos?: number;
  topPos?: number;
  forwardedRef?: React.Ref<HTMLDivElement>;
}

const AiAssistant = React.forwardRef<HTMLDivElement, AiAssistantProps>(
  function AiAssistant(props, forwardedRef) {
    const {
      leftPos = 0,
      topPos = 0
    } = props;

    const containerRef = React.useRef<HTMLDivElement>(null);
    const bottomRef = React.useRef<HTMLDivElement>(null);
    const messageEditorInputRef = React.useRef<HTMLInputElement>(null);

    const [containerEl, setContainerEl] = React.useState<HTMLDivElement | null>(null);
    const [popperAnchorEl, setPopperAnchorEl] = React.useState<null | HTMLElement>(null);
    const [popperPlacement, setPopperPlacement] = React.useState<PopperPlacementType>('top-end');
    const [inputText, setInputText] = React.useState<string>('');
    const [isSending, setIsSending] = React.useState<boolean>(false);
    const [respondingDelayElapsed, setRespondingDelayElapsed] = React.useState<boolean>(false);
    const [messages, setMessages] = React.useState<AssistantResponse[]>([]);
    const [dotCount, setDotCount] = React.useState<number>(1);

    const isOpen: boolean = Boolean(popperAnchorEl);
    const isMessageEmpty: boolean = inputText.trim().length === 0;
    const showResponding = isSending && respondingDelayElapsed;

    const session = useSession();
    const location = useLocation();
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
      id: 'draggable',
    });

    const scrollToBottom = (delayMs: number = 0) => {
      if (!bottomRef.current) return;
      setTimeout(() => {
        bottomRef?.current?.scrollIntoView({
          behavior: 'smooth',
          block: "nearest",
          inline: "nearest"
        });
      }, delayMs);
    };

    const handleContainerRef = React.useCallback((node: HTMLDivElement | null) => {
      containerRef.current = node;
      setContainerEl(node);
    }, []);

    React.useEffect(() => {
      let timeout: ReturnType<typeof setTimeout>;

      if (isSending) {
        timeout = setTimeout(() => {
          setRespondingDelayElapsed(true);
        }, 1000);
      }

      return () => {
        clearTimeout(timeout);
      };
    }, [
      isSending
    ]);

    React.useEffect(() => {
      if (showResponding) {
        const interval = setInterval(() => {
          setDotCount(prev => (prev % 3) + 1); // Cycle through 1, 2, 3
        }, 500); // Change every 500ms

        return () => clearInterval(interval);
      }
    }, [
      showResponding
    ]);

    React.useEffect(() => {
      scrollToBottom(100);
      messageEditorInputRef.current?.focus();
    }, [
      messages,
      showResponding
    ]);

    function handleTogglePopper(event: React.MouseEvent<HTMLElement>) {
      if (isDragging) return;
      if (popperAnchorEl) {
        setPopperAnchorEl(null);
      } else {
        setPopperAnchorEl(event.currentTarget);
      }
    }

    async function handleSendMessage() {
      const trimmedMessage = inputText.trim();
      if (!trimmedMessage) {
        console.warn('Message cannot be empty');
        return;
      }

      try {
        setRespondingDelayElapsed(false);
        setDotCount(1);
        setIsSending(true);

        setMessages(prev => [
          ...prev,
          AssistantResponse.create({
            responseId: undefined,
            role: Role.User,
            text: trimmedMessage,
            citations: [],
            timestamp: new AHDate()
          })
        ]);

        let lastResponseId: string | undefined = undefined;
        if (messages.length > 0) {
          const lastMessage = messages.at(-1);
          if (lastMessage?.responseId && lastMessage.responseId.length > 0) {
            lastResponseId = lastMessage.responseId;
          }
        }

        const aiService = new AIAssistantAPIService(session);
        const assistantResponse = await aiService.sendMessage(
          trimmedMessage,
          lastResponseId,
          location.pathname
        );

        setMessages(prev => [...prev, assistantResponse]);
      } catch (error) {
        if (error instanceof AIAssistantAPIServiceError) {
          console.error(`Error in AI Assistant API Service: ${error.message}`);
        } else {
          console.error('Unexpected error:', error);
        }
        return;
      } finally {
        setIsSending(false);
        setRespondingDelayElapsed(false);
        setInputText('');
      }
    }

    const getPopperTransformOrigin = (placement: PopperPlacementType) => {
      switch (placement) {
        case 'top-end':
          return 'right bottom';
        case 'top-start':
          return 'left bottom';
        case 'bottom-end':
          return 'right top';
        case 'bottom-start':
          return 'left top';
        default:
          return 'center center';
      }
    };

    return (
      <MainContainer
        ref={forwardedRef}
        dragging={isDragging}
        leftPos={leftPos}
        topPos={topPos}
        transformX={transform?.x}
        transformY={transform?.y}
        scaleX={transform?.scaleX}
        scaleY={transform?.scaleY}
        {...attributes}
      >
        <Popper
          anchorEl={popperAnchorEl}
          open={isOpen}
          placement="top-end"
          sx={{ zIndex: 2000 }}
          transition
          modifiers={[
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                padding: 16
              }
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['bottom-end', 'top-start', 'bottom-start'],
                padding: 8
              }
            }
          ]}
        >
          {({ TransitionProps, placement }) => {
            return (
              <ResizeHandlePlacementContext.Provider value={placement}>
                <PopperPlacementSync placement={placement} onChange={setPopperPlacement} />
                <Grow
                  {...TransitionProps}
                  timeout={300}
                  style={{
                    transformOrigin: getPopperTransformOrigin(placement)
                  }}
                  onEntered={() => scrollToBottom(0)}
                >
                  <PopperContainer ref={handleContainerRef}>
                    <ResizeHandle
                      handle="topLeft"
                      containerEl={containerEl}
                    >
                      <TopLeftResizeHandle />
                    </ResizeHandle>
                    <ResizeHandle
                      handle="top"
                      containerEl={containerEl}
                    >
                      <TopResizeHandle />
                    </ResizeHandle>
                    <ResizeHandle
                      handle="topRight"
                      containerEl={containerEl}
                    >
                      <TopRightResizeHandle />
                    </ResizeHandle>
                    <ResizeHandle
                      handle="right"
                      containerEl={containerEl}
                    >
                      <RightResizeHandle />
                    </ResizeHandle>
                    <ResizeHandle
                      handle="bottomRight"
                      containerEl={containerEl}
                    >
                      <BottomRightResizeHandle />
                    </ResizeHandle>
                    <ResizeHandle
                      handle="bottom"
                      containerEl={containerEl}
                    >
                      <BottomResizeHandle />
                    </ResizeHandle>
                    <ResizeHandle
                      handle="bottomLeft"
                      containerEl={containerEl}
                    >
                      <BottomLeftResizeHandle />
                    </ResizeHandle>
                    <ResizeHandle
                      handle="left"
                      containerEl={containerEl}
                    >
                      <LeftResizeHandle />
                    </ResizeHandle>
                    <Header>
                      <h3>AI Assistant</h3>
                      <CloseButton
                        aria-label="Close chat"
                        onClick={() => setPopperAnchorEl(null)}
                        size="medium"
                      >
                        <CloseIcon fontSize='medium' />
                      </CloseButton>
                    </Header>
                    <Content>
                      <Message sent={false}>
                        <MessageIcon>
                          <AssistantIcon fontSize="large" />
                        </MessageIcon>
                        <MessageText className="message-text">
                          {session.user?.name.firstName ? `${new AHDate().TimeOfDayGreeting}, ${session.user?.name.firstName}! ` : "Hello! "}
                          How can I help?
                        </MessageText>
                      </Message>
                      {messages.map((response: AssistantResponse) => (
                        <Message
                          sent={response.role === Role.User}
                          key={response.responseId ?? Guid.generate().value}
                        >
                          <MessageIcon>
                            {response.role === Role.User ? null : <AssistantIcon fontSize="large" />}
                          </MessageIcon>
                          <MessageText className="message-text">
                            <MarkdownStyles>
                              <MarkdownRenderer>
                                {response.text}
                              </MarkdownRenderer>
                            </MarkdownStyles>
                            {response.citations.length > 0 && (
                              <Citations
                                sx={{ mt: 2, mb: 1 }}
                                citations={response.citations}
                              />
                            )}
                            <Timestamp>
                              {response.timestamp.format('h:mm A')}
                            </Timestamp>
                          </MessageText>
                        </Message>
                      ))}
                      {isSending && showResponding && (
                        <Message sent={false}>
                          <MessageIcon>
                            <AssistantIcon fontSize="large" />
                          </MessageIcon>
                          <MessageText className="message-text">
                            {`Responding${'.'.repeat(dotCount)}`}
                          </MessageText>
                        </Message>
                      )}
                      <div ref={bottomRef} />
                    </Content>
                    <MessageEditor
                      inputRef={messageEditorInputRef}
                      maxRows={3}
                      variant="outlined"
                      placeholder="Type your message..."
                      fullWidth
                      disabled={isSending}
                      value={inputText}
                      onKeyDown={(event) => {
                        if (event.key === KeyboardKey.Enter && !event.shiftKey) {
                          event.preventDefault();
                          if (!isMessageEmpty) {
                            void handleSendMessage();
                          }
                        }
                      }}
                      onChange={(e) => setInputText(e.target.value)}
                      slotProps={{
                        input: {
                          autoComplete: "off",
                          endAdornment: (
                            <IconButton
                              aria-label="Send message"
                              edge="end"
                              color="primary"
                              disabled={isMessageEmpty || isSending}
                              onClick={handleSendMessage}
                            >
                              {isSending ?
                                <CircularProgress
                                  color="inherit"
                                  disableShrink
                                  size={24}
                                /> :
                                <SendIcon />
                              }
                            </IconButton>
                          )
                        }
                      }}
                    />
                  </PopperContainer>
                </Grow>
              </ResizeHandlePlacementContext.Provider>
            );
          }}
        </Popper>
        <ChatBubble
          ref={setNodeRef}
          aria-label="Chat with AI Assistant"
          color="primary"
          disableRipple
          title={isOpen ? "Close Chat" : "Chat with AI Assistant"}
          variant="extended"
          onClick={handleTogglePopper}
        >
          <DragHandle dragging={isDragging} {...listeners} />
          <ChatBubbleContent>
            <ChatBubbleIcon rotate={isOpen} top={popperPlacement?.includes('top') ?? false}>
              {isOpen ? <LastPageIcon /> : <AssistantIcon />}
            </ChatBubbleIcon>
            <ChatBubbleLabel>
              {isOpen ? "Minimize" : "Assistant"}
            </ChatBubbleLabel>
          </ChatBubbleContent>
        </ChatBubble>
      </MainContainer>
    )
  });

export default AiAssistant;
